import { FilePdfOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Modal,
  notification,
  Popconfirm,
  Row,
  Tooltip,
} from "antd";
import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FormEventValidate, {
  formNameEventValidate,
} from "../../components/forms/FormEventValidate/FormEventValidate";
import useSubmitValidateConstat from "../../components/forms/FormEventValidate/useSubmitValidateConstat";
import Breadcrumbs from "../../components/layout/BreadCrumbs";
import PageLayout from "../../components/layout/PageLayout/PageLayout";
import TableActions from "../../components/tables/TableActions/TableActions";
import Br from "../../components/ui/Br";
import spacing from "../../components/ui/spacingConfig";
import {
  useFetchAuditQuery,
  useFetchConstatQuery,
  useFetchUsersForFieldQuery,
} from "../../features/api/apiSlice";
import { useCrudAction } from "../../features/api/crud/hooks/useCrudAction";
import { useCrudConstat } from "../../features/api/crud/hooks/useCrudConstat";
import { modalProps } from "../../features/modals/modalProps";
import { useModal } from "../../features/modals/useModal";
import { useAppSelector } from "../../features/redux-store/hooks";
import { useRole } from "../../features/roles/useRole";
import styles from "./PageConstat.module.scss";
import CardCauseAnalysisConstat from "./components/CardCauseAnalysisConstat";
import CardDatePlaceConstat from "./components/CardDatePlaceConstat";
import CardDescriptionConstat from "./components/CardDescriptionConstat";
import CardPiloteGravityConstat from "./components/CardPiloteGravityConstat";
import CardComments from "../../components/ui/CardComments/CardComments";

const PageConstat: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isPilote, isAdmin, isUser } = useRole();
  const { user } = useAppSelector((state) => state.auth);

  /**
   * Fetch constat
   */
  const {
    data: constatData,
    isFetching,
    refetch: refetchConstat,
    error,
  } = useFetchConstatQuery(Number(id), {
    skip: !id,
  });

  const { data: auditData } = useFetchAuditQuery(
    constatData?.audit?.id as number,
    {
      skip: !constatData?.audit,
    },
  );

  /**
   * Handle 404 and 400's errors
   */
  useEffect(() => {
    const _error = error as { status?: number } | undefined;

    const notFound = _error?.status && _error?.status === 404;
    const basStatus = _error?.status && _error?.status >= 400;

    if (notFound) {
      notification.error({
        message:
          "Aucun constat trouvÃ©. Veuillez vÃ©rifier vos droits avec l'administrateur.",
      });
      navigate("/pdca/constat");
    } else if (basStatus) {
      notification.error({
        message:
          "Une erreur est survenue lors de la rÃ©cupÃ©ration du constat.",
      });
      navigate("/pdca/constat");
    }
  }, [error, navigate]);

  /**
   * Fetch "pilotes" (used to resolve the assigned pilote's display name)
   */
  const { data: pilotes } = useFetchUsersForFieldQuery("pilote");
  const { data: admins } = useFetchUsersForFieldQuery("sdlp-superadmin");

  let pilote = pilotes?.find((user) => user.id === constatData?.pilotes[0]?.id);
  // cas ou le pilote est directement un admin
  if (typeof pilote === "undefined") {
    pilote = admins?.find((user) => user.id === constatData?.pilotes[0]?.id);
  }

  const hasPilote = Boolean(pilote);

  /**
   * - Archive request
   * - Ask validation
   */
  const {
    onAskValidation,
    isAskingValidation,
    archiveConstat,
    isArchiving,
    onReopen,
    isReopening,
    exportPdf,
    isExporting,
    comments,
    createComment,
    isCreatingComment,
  } = useCrudConstat({
    navigate,
    fetchConstat: refetchConstat,
    constatId: Number(id),
  });

  /**
   * Close or refuse modal
   */
  const {
    onShowModal: showCloseOrRefuseModal,
    isModalOpen: isCloseOrRefuseModalOpen,
    onHideModal: onHideModalCloseOrRefuse,
  } = useModal();

  /**
   * Validate or refuse constat
   */
  const { onSubmit: validateOrRefuseOnSubmit, isLoading: isValidating } =
    useSubmitValidateConstat({
      id: Number(id),
      onSuccess: () => {
        onHideModalCloseOrRefuse();
        refetchConstat();
      },
    });

  /**
   * Navigation handlers
   */
  const navigateToEdit = useCallback(() => {
    navigate(`/constats/${id}/edit`);
  }, [id, navigate]);

  const navigateToActionNew = useCallback(() => {
    navigate(`/constats/${id}/actions/new`);
  }, [id, navigate]);

  /**
   * Delete action
   */
  const { onDelete: onDeleteAction, isDeleting: isDeletingAction } =
    useCrudAction(
      undefined,
      refetchConstat,
      Number(id),
      undefined,
      undefined,
      "constat",
    );

  /**
   * Loading
   */
  const isPageLoading =
    isFetching ||
    isValidating ||
    isArchiving ||
    isAskingValidation ||
    isReopening;

  const hasCauseAnalysis = Boolean(constatData?.causeAnalysis?.length);
  const canCreateAction = true;

  const iAmTheAssignedPilote = Boolean(
    constatData?.pilotes?.[0] &&
      user?.profile.sub &&
      constatData.pilotes[0].keycloakId === user.profile.sub,
  );

  return (
    <PageLayout pageContainerClassName={styles.root} isLoading={isPageLoading}>
      {/**
       * Top (breadcrumbs, and action buttons)
       */}
      {!isPageLoading && (
        <Row justify="space-between" align="middle">
          {!isFetching && (
            <Col>
              <Breadcrumbs
                pageName="constat"
                constatName={constatData?.name}
                constatId={constatData?.id}
              />
            </Col>
          )}
          <Col>
            <Row gutter={spacing.m} className={styles.buttonsContainer}>
              {isAdmin && (
                <Col>
                  <Button type="primary" onClick={navigateToEdit}>
                    Modifier
                  </Button>
                </Col>
              )}
              {isPilote && (
                <Col>
                  <Popconfirm
                    title="Confirmez-vous la demande de validation de ce constat?"
                    onConfirm={() => onAskValidation(constatData?.id || 0)}
                    okText="Oui, demander"
                    cancelText="Annuler"
                  >
                    <Tooltip
                      title={
                        !constatData?.canBeValidated
                          ? constatData?.noValidationMessage
                          : undefined
                      }
                    >
                      <span>
                        <Button
                          type="primary"
                          disabled={
                            !constatData?.canBeValidated || isAskingValidation
                          }
                        >
                          Demander la validation
                        </Button>
                      </span>
                    </Tooltip>
                  </Popconfirm>
                </Col>
              )}
              {isAdmin && (
                <Col>
                  <Button
                    type="primary"
                    onClick={showCloseOrRefuseModal}
                    disabled={!hasPilote}
                  >
                    ClÃ´turer ou refuser
                  </Button>
                </Col>
              )}
              {isAdmin && (
                <Col>
                  <Button
                    type="primary"
                    disabled={constatData?.statut !== "CLOTURE"}
                    onClick={() => onReopen(constatData?.id || 0)}
                  >
                    RÃ©ouvrir
                  </Button>
                </Col>
              )}
              {isAdmin && (
                <Col>
                  <Popconfirm
                    title="Confirmez-vous l'archivage de ce constat?"
                    onConfirm={() => archiveConstat(constatData?.id || 0)}
                    okText="Oui, archiver"
                    cancelText="Annuler"
                  >
                    <Button type="primary" disabled={!hasPilote}>
                      Archiver
                    </Button>
                  </Popconfirm>
                </Col>
              )}
              <Col>
                <Button
                  type="primary"
                  icon={<FilePdfOutlined />}
                  onClick={() => exportPdf(Number(id))}
                  loading={isExporting}
                >
                  PDF
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      <Br height={spacing.sm} />
      {/**
       * Cards Description and DateAndPlace
       */}
      <Row gutter={spacing.m} className={styles.firstRow}>
        <Col span={spacing.s}>
          <CardDescriptionConstat
            constatData={constatData}
            className={styles.cardsEventDetail}
          />
        </Col>
        <Col span={spacing.xxs} className={styles.colDate}>
          <CardDatePlaceConstat
            constatData={constatData}
            audit={auditData}
            className={styles.cardsEventDetail}
          />
        </Col>
      </Row>
      {/**
       * Cards Pilote, CauseAnalysis and Actions
       */}
      {pilote && (
        <>
          <Br height={spacing.xl} />
          <Row gutter={spacing.m} className={styles.secondRow}>
            <Col span={spacing.xxs} className={styles.colPilote}>
              <CardPiloteGravityConstat
                constatData={constatData}
                pilote={pilote}
                className={styles.cardsEventDetail}
              />
            </Col>
            <Col span={spacing.s}>
              <CardCauseAnalysisConstat
                constatData={constatData}
                refetchConstat={refetchConstat}
                className={`${styles.cardsEventDetail} ${styles.cardCauseAnalysis}`}
                canAddCauseAnalysis={isPilote}
                hasAnalysis={!!hasCauseAnalysis}
              />
            </Col>
          </Row>
          <Br height={spacing.xl} />
          <Row>
            <Col span={spacing.m} className={styles.colActionPlan}>
              <div className={styles.actionHeader}>
                <h3>Plan d'action</h3>
                <Button
                  type="primary"
                  onClick={navigateToActionNew}
                  disabled={!canCreateAction || isUser}
                >
                  Ajouter une nouvelle action
                </Button>
              </div>
              <Br height={spacing.sm} />
              <div className={styles.cardsEventDetail}>
                <TableActions
                  iAmTheAssignedPilote={iAmTheAssignedPilote}
                  actionsData={constatData?.actions}
                  fetchData={refetchConstat}
                  dontFetchOnMount
                  isLoading={isFetching || isDeletingAction}
                  onDelete={onDeleteAction}
                  isColumnForPageEvent={true}
                  parentType="constat"
                />
              </div>
            </Col>
          </Row>
        </>
      )}
      <Br height={spacing.xl} />
      <Row>
        <Col span={spacing.m}>
          <CardComments
            comments={comments}
            onAddComment={createComment}
            isSubmitting={isCreatingComment}
            className={styles.cardsEventDetail}
          />
        </Col>
      </Row>
      <Br height={spacing.xl} />
      {/**
       * Modal Close or Refuse
       */}
      <Modal
        {...modalProps}
        open={isCloseOrRefuseModalOpen}
        onCancel={onHideModalCloseOrRefuse}
        loading={isValidating}
        title={<p>ClÃ´turer ou refuser</p>}
        footer={
          <>
            <Button
              type="default"
              onClick={onHideModalCloseOrRefuse}
              disabled={isValidating}
            >
              Annuler
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              disabled={isValidating}
              loading={isValidating}
              form={formNameEventValidate}
            >
              Soumettre
            </Button>
          </>
        }
      >
        <FormEventValidate onSubmit={validateOrRefuseOnSubmit} />
      </Modal>
    </PageLayout>
  );
};

export default PageConstat;
